/**
 * English strings — the source catalogue. Every user-visible string in the app lives
 * here (PRD "Local language"). Add a language by copying hi.ts; TypeScript enforces
 * that every key is translated. Placeholders use {name}.
 */
export const en = {
  app: {
    name: 'Assy Readiness',
    product: 'SaarthiOS CLEAR',
    tagline: 'Checks for Line Execution & Assembly Readiness',
  },
  common: {
    units: 'units',
    of: 'of',
    back: 'Back',
    cancel: 'Cancel',
    save: 'Save',
    close: 'Close',
    confirm: 'Confirm',
    undo: 'Undo',
    undone: 'Undone',
    retry: 'Try again',
    loading: 'Loading…',
    change: 'Change',
    edit: 'Edit',
    add: 'Add',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    today: 'Today',
    shift: 'Shift {code}',
    ext: 'Ext {ext}',
    call: 'Call',
    by: 'by {name}',
    at: 'at {time}',
    delete: 'Delete',
    show: 'Show',
    none: '—',
    optional: 'optional',
    somethingWrong: 'Something went wrong. Please try again.',
    notFound: 'Page not found',
    goHome: 'Go to my work',
    notAllowed: 'This screen is not part of your role.',
    offlineRead: 'Offline — showing the last saved data.',
  },
  roles: {
    planner: 'Planner',
    paint: 'Paint',
    rm: 'RM',
    quality: 'Quality',
    supervisor: 'Line Supervisor',
    mgmt: 'Management',
    admin: 'Admin',
  },
  gates: {
    paint: 'Paint',
    rm: 'RM',
    quality: 'Quality',
    short: { paint: 'P', rm: 'R', quality: 'Q' },
  },
  gateStatus: {
    ok: 'OK',
    partial: { paint: 'Partial', rm: 'Partial', quality: 'Partial hold' },
    blocked: { paint: 'Short', rm: 'Not available', quality: 'Hold' },
    pending: 'Not updated',
    locked: 'Locked',
    observation: 'OK with observation',
  },
  status: {
    READY: 'Ready',
    PART: 'Part ready',
    WAIT: 'Wait',
    HOLD: 'Hold',
    STARTED: 'Started',
    CANCELLED: 'Cancelled',
  },
  priority: { high: 'High', normal: 'Normal', low: 'Low', highPriority: 'High priority' },
  login: {
    tapYourName: 'Tap your name',
    enterPin: 'Enter your 4-digit PIN',
    wrongPin: 'Wrong PIN. {n} tries left before the account locks for 15 minutes.',
    wrongPinShort: 'Wrong PIN. Try again.',
    locked: 'Too many wrong PINs. Locked until {time}. Ask the Admin to unlock.',
    notYou: 'Not you?',
    firstTime: 'First time here? Set up your PIN',
    noPin: 'You have not set a PIN yet.',
    setUpNow: 'Set up now',
    sharedDevice: '{label} · shared {role} device',
    pairTitle: 'Set up as a shared department device',
    pairHint: 'Enter the 6-digit code from Admin → Devices.',
    pairButton: 'Pair this device',
    pairDone: 'This device is now the {role} department device.',
    demoPin: 'Demo PIN for every user: {pin}',
    loginFailed: 'Could not log in. Check the connection and try again.',
    welcomeBack: 'Welcome back',
    plantLine: '{plant} · {line} · {shift} · {date}',
  },
  setup: {
    title: 'First-time setup',
    mobile: 'Your mobile number',
    mobileHint: 'The number the Admin registered for you.',
    sendOtp: 'Send code',
    otpSentTo: 'We sent a 6-digit code to {mobile}.',
    enterOtp: 'Enter the 6-digit code',
    demoOtp: 'Demo code: {code}',
    verify: 'Verify',
    choosePin: 'Choose a 4-digit PIN',
    confirmPin: 'Enter the same PIN again',
    pinsDontMatch: 'The PINs do not match. Start again.',
    weakPin: 'Too easy to guess. Pick another PIN.',
    done: 'PIN set. You are logged in.',
    wrongOtp: 'Wrong code. Check the SMS and try again.',
    expired: 'That code expired. Ask for a new one.',
    resend: 'Send a new code',
    step: 'Step {n} of 3',
  },
  shell: {
    noLine: 'No active assembly line',
    noLineHint:
      'Plans and work lists appear once a line is turned on. Ask your Admin to turn one on in Setup → Lines.',
    noLineAdmin: 'Turn a line on in Setup → Lines, then the Planner can publish today’s plan.',
    openLines: 'Open Lines',
    synced: 'Synced',
    syncing: 'Syncing…',
    pending: '{n} to sync',
    offline: 'Offline',
    offlineQueued: 'Offline — your taps are saved and will sync.',
    waitingOnYou: '{n} waiting',
    logout: 'Log out',
    language: 'Language',
    workingAs: 'Working as',
    profile: 'Profile',
    sessionEnds: 'Session ends at {time}',
    sharedIdle: 'Logs out after 15 minutes without use',
    me: 'Me',
    views: 'Views',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
    sync: 'Sync',
    allSaved: 'All changes saved · {time}',
    shiftRow: 'Shift',
    line: 'Line',
    updateReady: 'A new version is ready.',
    reload: 'Reload',
    liveOff: 'Live updates paused — reconnecting…',
    sessionEnded: 'Your session ended. Enter your PIN again.',
    theme: 'Theme',
    themeAuto: 'Auto',
    themeLight: 'Light',
    themeDark: 'Dark',
    version: 'Version {v}',
  },
  floor: {
    myWork: 'My work · {gate}',
    todo: 'To Do',
    issues: 'Issues',
    done: 'Done',
    allOk: 'All OK ({n})',
    confirmAllOk: 'Tap again to mark {n} lines OK',
    waitingFor: 'Waiting for Paint / RM',
    toInspect: 'to inspect',
    clearableOf: 'of {n} · to inspect',
    pendingSince: 'Oldest waiting since {time} ({age})',
    keyboardHint: 'Keys: ↑ ↓ move · O OK · P partial · S short/hold · A all OK',
    allClear: 'All clear',
    noTodo: 'Nothing waiting. New lines show up here as soon as the Planner publishes them.',
    noIssues: 'No open issues',
    noIssuesHint: 'Lines you mark Partial or {bad} stay here until you update them.',
    noDone: 'Nothing marked yet',
    noDoneHint: 'Lines you mark OK move here.',
    plusAdded: '+{n} added to plan',
    moreArrived: '+{n} more to inspect',
    whyTitle: '{label}: why?',
    oneTapSaves: 'One tap saves.',
    partialTitle: '{label}: how many are OK?',
    mustBeLess: 'Must be less than {max}. For all {max}, tap OK.',
    splitComing: '{ok} OK, {rest} still to come',
    splitHeld: '{ok} OK, {rest} held',
    reasonForHeld: 'Reason for the held units',
    observationTitle: 'Add an observation (optional)',
    lockedQuality: 'Quality has checked this. Call {name}, Ext {ext}.',
    lockedStarted: 'Line has started this model. Call {name}, Ext {ext}.',
    lockedGeneric: 'Locked',
    saved: '{model}: {what}',
    markedOk: '{n} lines marked {gate} OK',
    planNotPublished: 'Today’s plan is not published yet.',
    planNotPublishedHint: 'The Planner is still preparing it. It appears here the moment it is published.',
    noPlan: 'No plan for this shift yet.',
    edit: 'Change answer',
    cancelEdit: 'Keep answer',
  },
  planner: {
    title: 'Today’s plan',
    planFor: 'Plan · {line} · {shift} · {date}',
    emptyTitle: 'No plan for this shift yet',
    emptyHint: 'Start from the last plan and adjust, or build a new one.',
    copyFrom: 'Copy plan from {date} ({n} lines)',
    copyYesterday: 'Copy yesterday’s plan',
    startNew: 'Start new plan',
    publish: 'Publish',
    publishConfirm: 'Tap again to publish {n} lines to every department',
    published: 'Published {time} by {name}. Edits go live to every department at once.',
    draft: 'Draft — departments do not see this plan until you publish it.',
    addLine: 'Add line',
    pickModel: 'Tap the model and colour',
    mostUsed: 'Most used',
    allModels: 'All models',
    howMany: 'How many?',
    addToPlan: 'Add to plan',
    added: '{model} {qty} added. {who} see it now.',
    addedDraft: '{model} {qty} added to the draft.',
    departments: 'Paint and RM',
    changeQty: 'Change qty',
    newQty: 'New plan quantity',
    nowQty: 'now {n}',
    qtyUpHint: 'Departments that cleared the old quantity get the extra units back on their list.',
    qtyDownHint: 'Cleared quantities are capped at the new plan.',
    qtyChanged: '{model}: plan now {qty}',
    cancelLine: 'Cancel line',
    tapAgainCancel: 'Tap again to cancel this line',
    cancelled: '{model} removed from every department’s list',
    runOrderNote:
      'Run order is top to bottom. Drag or use the arrows to reorder. Tap a model for its history.',
    priorityHint: 'Tap to change priority',
    moveUp: 'Move up',
    moveDown: 'Move down',
    drag: 'Drag to reorder',
    plan: 'Plan',
    ready: 'Ready',
    waiting: 'Waiting',
    onHold: 'On hold',
    dashboard: 'Dashboard',
    otherDay: 'Other day',
    currentShift: 'Current shift',
    viewing: 'Viewing {date} · {shift}',
    notCurrent: 'This is not the running shift.',
    noModels: 'No models yet',
    noModelsHint: 'Ask the Admin to add the models you assemble (Setup → Models).',
  },
  supervisor: {
    title: 'Ready to run',
    comingUp: 'Coming up',
    readyTab: 'Ready',
    started: 'Started',
    start: 'Start',
    nothingReady: 'Nothing ready yet',
    nothingReadyHint: 'Lines appear here the moment Paint, RM and Quality clear them.',
    nothingComing: 'Nothing waiting',
    nothingComingHint: 'Every line has a ready quantity.',
    noneStarted: 'No lines started',
    noneStartedHint: 'Tap Start on a ready line.',
    readyOf: 'ready of {qty} planned',
    readyFull: 'ready, full plan',
    startedToast: '{model} started on {line}',
    newlyReady: 'Just cleared',
    inRunOrder: '{line} · in run order set by the Planner',
  },
  dashboard: {
    title: 'Live dashboard',
    subtitle: '{line} · {shift} · {date} · updates as departments tap',
    todaysPlan: 'Today’s plan',
    planLines: '{n} lines',
    ready: 'Ready',
    pctOfPlan: '{p}% of plan',
    atGate: 'At {gate}',
    onHold: '{n} on hold',
    waiting: '{n} waiting',
    clear: 'Clear',
    whereTitle: 'Where today’s {n} units stand',
    legendReady: 'Ready',
    legendWait: 'Waiting for update',
    legendHold: 'On hold',
    whereNote:
      'Every unit not ready is counted once, against the gate holding it back. The four groups always add up to the plan.',
    lines: 'Plan lines',
    linesFiltered: 'Plan lines · {filter}',
    filterReady: 'with ready qty',
    filterGate: 'held at {gate}',
    showAll: 'Show all',
    rowHint: 'Tap a row for its history',
    colModel: 'Model',
    colPlan: 'Plan',
    colGates: 'P · R · Q',
    colReady: 'Ready',
    colStatus: 'Status',
    colWhy: 'Held up by',
    holds: 'On hold · {n}',
    noHolds: 'No holds right now.',
    holdSince: 'Since {time} ({age}) · Owner {name}',
    waitingUpdate: 'Waiting for an update · {n}',
    noneWaiting: 'Every department is up to date.',
    progress: 'Department progress',
    progressNote: 'Units each department has cleared, as a share of the plan.',
    overview: 'Overview',
    holdsTab: 'Holds',
    whyHold: '{gate} · {reason} · {qty} held {age}',
    whyPartial: '{gate} · {qty} short of plan · {age}',
    whyPending: '{gate} · not updated · {age}',
    whyStarted: 'Started {time}',
    reports: 'Reports',
    unbalanced: 'Totals do not add up — please report this to IT.',
  },
  laptop: {
    name: 'Floor Display',
    title: '{line} readiness',
    subtitle: '{plant} · {shift} · {date}',
    fullscreen: 'Full screen',
    exit: 'Exit',
    open: 'Open Floor Display',
    cta: 'Full-screen live board for the shop-floor monitor or TV — open it on the laptop that drives the screen.',
  },
  timeline: {
    title: 'History',
    gates: 'Gates',
    readyOf: 'Ready {r} of {q} · {p} priority',
    waitingOwner: 'Waiting {age} · {name}, Ext {ext}',
    lockedHint: 'Opens when Paint and RM clear some units',
    cleared: '{n} cleared',
    undone: 'undone',
    'plan.publish': 'Plan published',
    'plan.create': 'Plan created',
    'item.add': 'Added to plan',
    'item.qty': 'Quantity changed {from} → {to}',
    'item.priority': 'Priority set to {to}',
    'item.cancel': 'Line cancelled',
    'item.start': 'Line started',
    'item.unstart': 'Start undone',
    'plan.reorder': 'Run order changed',
    gateOk: '{gate} OK',
    gatePartial: '{gate} partial: {ok} of {qty}',
    gateQPartial: 'Quality partial hold: {ok} OK, rest held ({reason})',
    gateBlocked: '{gate} {label} ({reason})',
    gateObservation: 'Quality OK with observation ({reason})',
  },
  reports: {
    title: 'Reports',
    daily: 'Daily readiness summary',
    date: 'Date',
    from: 'From',
    to: 'To',
    downloadPdf: 'Download PDF',
    downloadXlsx: 'Download Excel',
    pareto: 'Reason Pareto (7 days)',
    paretoNone: 'No holds or shortages in this period.',
    count: 'Times',
    qty: 'Units',
    ttc: 'Average time to clear',
    ttcNote: 'From the moment a gate could act until it cleared the full quantity.',
    samples: '{n} lines',
    pilot: 'Pilot metrics',
    pilotNote: 'PRD success metrics for the pilot, measured automatically.',
    within30: 'Lines updated by every gate within 30 min of publish',
    firstReady: 'Median publish → first READY line',
    activeRoles: 'Daily active users per floor role',
    balanced: 'Days where dashboard total = plan total',
    target: 'Target {t}',
    minutes: '{n} min',
    noData: 'No data yet',
    shiftTotals: 'Shift {code}: plan {plan}, ready {ready}',
    rostered: '{a} of {r} active',
    perDay: 'Per day',
  },
  admin: {
    title: 'Setup',
    subtitle: 'Users, models, reasons, shifts and devices for {plant}',
    users: 'Users',
    models: 'Models',
    reasons: 'Reasons',
    shifts: 'Shifts',
    lines: 'Lines',
    devices: 'Shared devices',
    audit: 'Audit log',
    settings: 'Plant',
    addUser: 'Add user',
    editUser: 'Edit user',
    name: 'Name',
    mobile: 'Mobile',
    titleField: 'Job title',
    department: 'Department',
    extField: 'Extension',
    rolesField: 'Roles',
    languageField: 'Language',
    initialPin: 'Set a PIN now (for plant-issued phones)',
    initialPinHint: 'Leave empty and the user sets their own PIN with an OTP.',
    resetPin: 'Reset PIN',
    resetPinConfirm: 'Tap again: {name} sets a new PIN at next login',
    pinReset: 'PIN reset for {name}. They set a new one at next login.',
    unlock: 'Unlock',
    unlocked: '{name} unlocked',
    lockedUntil: 'Locked until {time}',
    noPinYet: 'PIN not set',
    deactivate: 'Deactivate',
    activate: 'Activate',
    inactive: 'Inactive',
    photo: 'Photo',
    uploadPhoto: 'Upload photo',
    saved: 'Saved',
    addModel: 'Add model',
    modelName: 'Model name',
    sweep: 'Sweep (mm)',
    finish: 'Colour / finish',
    colour: 'Swatch colour',
    blades: 'Blades',
    addReason: 'Add reason',
    reasonEn: 'Label (English)',
    reasonHi: 'Label (Hindi)',
    icon: 'Icon',
    maxReasons: 'At most 6 active per gate.',
    addShift: 'Add shift',
    code: 'Code',
    start: 'Start',
    end: 'End',
    addLine: 'Add line',
    addDevice: 'Add shared device',
    deviceLabel: 'Device name',
    deviceRole: 'Department',
    pairingCode: 'Pairing code: {code}',
    pairingHint:
      'On the device, open the login screen → “Set up as a shared department device” and enter this code. Valid 24 h.',
    newCode: 'New code',
    paired: 'Paired {time}',
    notPaired: 'Not paired yet',
    lastSeen: 'Last seen {time}',
    unpair: 'Unpair',
    plantName: 'Plant name',
    timezone: 'Timezone',
    auditWhen: 'When',
    auditWho: 'Who',
    auditWhat: 'Action',
    auditEntity: 'Record',
    loadMore: 'Load more',
    active: 'Active',
    required: 'Required',
  },
  errors: {
    forbidden: 'Your role cannot do that.',
    not_found: 'That line no longer exists.',
    conflict: 'Someone else changed this. The screen is updated.',
    cancelled: 'The Planner cancelled this line.',
    started: 'The line has already started this model.',
    quality_acted: 'Quality has already checked this line.',
    quality_locked: 'Quality opens once Paint and RM clear some units.',
    qty_out_of_range: 'That quantity is not possible.',
    reason_required: 'Pick a reason.',
    reason_not_allowed: 'No reason needed for OK.',
    reason_invalid: 'That reason was removed. Pick another.',
    undo_expired: 'Too late to undo. Use Change instead.',
    undo_not_allowed: 'This can no longer be undone.',
    not_ready: 'Nothing ready on this line yet.',
    plan_exists: 'A plan for this shift already exists.',
    plan_not_draft: 'The plan is already published.',
    plan_draft: 'The plan is not published yet.',
    plan_empty: 'Add at least one line before publishing.',
    no_copy_source: 'No earlier plan to copy.',
    model_inactive: 'That model is no longer active.',
    invalid: 'Could not save. Please try again.',
    network: 'No connection. Saved on this device; it will sync.',
    rejected: 'Not saved: {reason}',
  },
  welcome: {
    title: 'Set up your plant',
    intro: 'A fresh CLEAR server. Create the plant and the first Admin — it takes a minute.',
    step: 'Step {n} of 3',
    codeTitle: 'Setup code',
    codeHint: 'The 6-digit code shared with you by the team.',
    codeWrong: 'That setup code is not right. Check the code the team shared with you.',
    plantTitle: 'Plant',
    plantName: 'Plant name',
    plantNameHint: 'As people call it, e.g. “Haridwar Fan Plant”.',
    lineName: 'First assembly line',
    lineNameHint: 'You can add more lines later.',
    timezone: 'Timezone',
    starterReasons: 'Start with the standard reason chips (Paint, RM, Quality)',
    adminTitle: 'First Admin',
    adminName: 'Admin name',
    adminMobile: 'Admin mobile number',
    adminMobileHint: 'The Admin logs in with this number, a one-time code and a 4-digit PIN.',
    adminJobTitle: 'Job title',
    create: 'Create plant and Admin',
    next: 'Next',
    back: 'Back',
    done: 'Plant created. Now activate the Admin account.',
    otpDefault: 'OTP is the default code {code} for now (no SMS yet).',
    already: 'This plant is already set up.',
  },
  onboarding: {
    title: 'Getting started',
    intro: 'Finish these once and the floor can start using CLEAR.',
    users: 'Add people and give each one a role',
    usersHint: 'Planner, Paint, RM, Quality, Line Supervisor, Management.',
    models: 'Add the models you assemble',
    modelsHint: 'Name, sweep size, colour and a photo.',
    reasons: 'Check the reason chips for each gate',
    shifts: 'Check the shift timings',
    devices: 'Pair shared department phones (optional)',
    activate: 'Each person opens the app → “First time here?” → mobile → OTP {code} → PIN.',
    go: 'Open',
    hide: 'Hide',
    userAdded: '{name} added. They open the app → First time here → their mobile → OTP → choose a PIN.',
  },
  demo: {
    badge: 'Demo',
    banner: 'Demo data — sample plant with PIN 1234 for everyone. Nothing here is real.',
    tryIt: 'Try the demo with sample data →',
    reset: 'Reset demo data',
    realApp: 'Go to the real app',
    resetDone: 'Demo reset to the sample plant.',
  },
  landing: {
    login: 'Log in',
    tryDemo: 'Try the demo',
    headline: 'Know how much of today’s plan can run — before the line starts.',
    lede: 'Paint, RM and Quality clear their gate in one to three taps. Every plan line gets a live READY, WAIT or HOLD status, and the Production Head sees one ready number everyone trusts — no follow-up calls.',
    heroNote: 'Phone or laptop browser · nothing to install · English and हिंदी',
    howTitle: 'One rule, one number',
    howLede: 'A line can run only when painted parts, raw material and Quality clearance are all in place.',
    gatePaint: 'Paint',
    gatePaintText: 'Paint shop marks OK, Partial or Short.',
    gateRm: 'RM',
    gateRmText: 'Stores marks OK, Partial or Not available.',
    gateQuality: 'Quality',
    gateQualityText: 'QA inspects what Paint and RM have cleared.',
    gateReady: 'Ready',
    gateReadyText: 'Ready qty = the lowest cleared quantity. The dashboard always adds up to the plan.',
    rolesTitle: 'One screen per role',
    rolesLede: 'Everyone lands on their own work list straight after the PIN. No menus to learn.',
    rolePlanner: 'Publishes today’s plan in three taps: copy yesterday, adjust, publish.',
    roleGates: 'Paint, RM, Quality: tap OK, or pick a reason chip. Undo within 10 seconds.',
    roleSupervisor: 'Line Supervisor sees what is ready to run, in order, and taps Start.',
    roleMgmt: 'Management sees ready vs plan, every hold with who to call, and daily reports.',
    roleAdmin: 'Admin sets up people, models and reasons, and puts the Floor Display on a monitor.',
    floorTitle: 'Built for the shop floor',
    f1: '1 to 3 taps',
    f1Text: 'OK is a single tap. No typing on the floor — number pads and reason chips.',
    f2: 'Works offline',
    f2Text: 'Taps are saved on the phone during a network drop and sync on their own.',
    f3: 'Live everywhere',
    f3Text: 'Every screen updates within seconds when any department taps.',
    f4: 'Readable at a glance',
    f4Text: 'Colour, icon and word on every status, big targets for gloved hands, Hindi and English.',
    f5: 'Floor Display',
    f5Text: 'A full-screen board for the shop-floor monitor — all lines, holds and progress.',
    f6: 'Secure by design',
    f6Text: 'PIN login, lockout after 5 tries, every change audited, data hosted in India.',
    startTitle: 'Getting a plant started',
    s1: 'Set up the plant',
    s1Text: 'The first Admin creates the plant, its line and shifts.',
    s2: 'Add people and roles',
    s2Text: 'Admin adds each person with their mobile and role, plus the models you assemble.',
    s3: 'Everyone activates',
    s3Text: 'Mobile → one-time code → a 4-digit PIN. From then on, just the PIN.',
    ctaTitle: 'See it with a sample plant',
    ctaText:
      'The demo runs a Havells ceiling-fan plan with 8 lines and 12,000 units. Every sample user’s PIN is 1234.',
    footer: 'SaarthiOS CLEAR · AI Gurukul Pvt Ltd',
    previewTitle: 'Line 2 · Shift A',
    previewReady: 'Ready',
    previewPlan: 'Plan',
    previewHold: 'On hold',
  },
  trial: {
    title: 'End of Trial Period',
    body: 'Your trial of SaarthiOS CLEAR has ended. Contact admin for renewing the access.',
    logout: 'Log out',
  },
  a11y: {
    delete: 'Delete digit',
    menu: 'Open profile',
    close: 'Close',
    skip: 'Skip to content',
  },
} as const;

type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };
export type Messages = Widen<typeof en>;
